import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus,
  X,
  Save,
  Edit3,
  Trash2,
  CalendarDays,
  MapPin,
  FileText,
  Sparkles,
  Ticket as TicketIcon,
} from 'lucide-react';

import { ActionButton, SectionHeader, StatusBadge } from '../components/VisualAtoms';
import ExhibitionPublicUrlStrip from '../components/ExhibitionPublicUrlStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Strip empty strings so cleared inputs don't crash backend type coercion
// (e.g. '' isn't valid for a Postgres date column). Booleans/false are kept.
function cleanForApi(form) {
  const out = {};
  for (const [k, v] of Object.entries(form || {})) {
    if (v === '' || v === undefined) continue;
    out[k] = v;
  }
  return out;
}

function serverError(err, fallback) {
  return err?.response?.data?.detail || fallback;
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'live', label: 'Live' },
  { value: 'closed', label: 'Closed' },
];

const EMPTY_FORM = {
  title: '',
  slug: '',
  theme: '',
  statement: '',
  description: '',
  ai_context_notes: '',
  location_name: '',
  location_address: '',
  city: '',
  country: '',
  start_date: '',
  end_date: '',
  open_call_deadline: '',
  status: 'draft',
  ticket_requests_enabled: true,
  open_call_enabled: true,
  ticket_mode: 'standard',
  baf_ticket_url: '',
};

function Field({ label, helper, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase">{label}</span>
      {helper && (
        <span className="block text-[10px] font-mono text-[#555] mt-0.5">{helper}</span>
      )}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const inputCls =
  'w-full bg-transparent border border-[#222] text-white text-xs font-mono px-3 py-2 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#444]';
const textareaCls =
  'w-full bg-transparent border border-[#222] text-white text-xs font-mono p-2.5 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#444] resize-none';

function Toggle({ label, value, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      data-clickable="true"
      data-testid={testId}
      className={`group flex items-center gap-3 border px-3 py-2 transition-colors ${
        value
          ? 'border-[#90E0EF]/40 text-[#90E0EF] bg-[#90E0EF]/5'
          : 'border-[#222] text-[#888] hover:text-white'
      }`}
    >
      <span
        className={`w-7 h-3.5 rounded-full relative transition-colors ${
          value ? 'bg-[#90E0EF]/55' : 'bg-[#222]'
        }`}
      >
        <span
          className={`absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-all ${
            value ? 'left-3.5' : 'left-0.5'
          }`}
        />
      </span>
      <span className="text-[10px] font-mono uppercase tracking-[0.14em]">{label}</span>
    </button>
  );
}

function ExhibitionForm({ initial, onSubmit, onCancel, mode = 'create', busy }) {
  // Convert ISO timestamps (e.g. '2026-08-10T00:00:00+00:00') coming back
  // from the API into the yyyy-MM-dd shape that <input type="date">
  // requires — otherwise the browser logs a warning per keystroke and
  // strips the value.
  const toDateInput = (v) => {
    if (!v) return '';
    const s = String(v);
    return s.length >= 10 ? s.slice(0, 10) : s;
  };

  const [form, setForm] = useState(initial || EMPTY_FORM);
  // On CREATE, the slug auto-tracks the title until the user manually
  // touches the slug field. On EDIT we leave the existing slug alone
  // so shared URLs never break when a title changes.
  const [slugTouched, setSlugTouched] = useState(mode !== 'create');

  // Two-tier slugifier (defined before the useEffect that uses it):
  //   `slugifyLive`  — while typing: lowercase, ASCII, replace non-safe chars
  //                    with '-', collapse runs. Does NOT strip the trailing
  //                    dash so word boundaries survive as the user types
  //                    ('test '  →  'test-', then 'w'  →  'test-w').
  //   `slugifyFinal` — on submit / blur / auto-derive: also strip leading &
  //                    trailing dashes. This is the shape the URL takes.
  const slugifyLive = (s) =>
    (s || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-');

  const slugifyFinal = (s) => slugifyLive(s).replace(/^-+|-+$/g, '');

  useEffect(() => {
    // Defense-in-depth: if the DB row has a stale non-URL-safe slug
    // (e.g. legacy data with spaces), normalize it here so the form
    // never re-submits the dirty value. `slugifyFinal` is a no-op on
    // already-clean slugs.
    const raw = initial || EMPTY_FORM;
    const cleanSlug = raw.slug ? slugifyFinal(raw.slug) : '';
    setForm({ ...raw, slug: cleanSlug || raw.slug || '' });
    setSlugTouched(mode !== 'create');
  }, [initial, mode]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onTitleChange = (v) => {
    if (!slugTouched) {
      set({ title: v, slug: slugifyFinal(v) });
    } else {
      set({ title: v });
    }
  };

  const onSlugChange = (v) => {
    setSlugTouched(true);
    set({ slug: slugifyLive(v) });
  };

  const onSlugBlur = () => {
    // Normalize to the URL shape when the user leaves the field.
    setForm((f) => ({ ...f, slug: slugifyFinal(f.slug) }));
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="space-y-6"
      data-testid={`exhibition-form-${mode}`}
    >
      {/* Identity */}
      <section className="space-y-3">
        <SectionHeader title="Identity" helper="Title and slug for routing." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Title *">
            <input
              required
              value={form.title}
              onChange={(e) => onTitleChange(e.target.value)}
              className={inputCls}
              data-testid="exhibition-field-title"
            />
          </Field>
          <Field label="Slug *" helper="Used in the public URL (/exhibitions/<slug>). Auto-normalized to url-safe kebab-case.">
            <input
              required
              value={form.slug}
              onChange={(e) => onSlugChange(e.target.value)}
              onBlur={onSlugBlur}
              className={inputCls}
              placeholder="this-is-humanity-2026"
              data-testid="exhibition-field-slug"
            />
          </Field>
          <Field label="Theme">
            <input
              value={form.theme || ''}
              onChange={(e) => set({ theme: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Status">
            <select
              value={form.status || 'draft'}
              onChange={(e) => set({ status: e.target.value })}
              className={`${inputCls} bg-[#0a0a0a]`}
              data-testid="exhibition-field-status"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Field>
        </div>
      </section>

      {/* Curatorial */}
      <section className="space-y-3">
        <SectionHeader
          title="Curatorial"
          helper="Public-facing concept and the AI context that drives idea analysis."
        />
        <Field
          label="Statement"
          helper="Curatorial statement — concept, emotional direction, artistic thesis."
        >
          <textarea
            rows={4}
            value={form.statement || ''}
            onChange={(e) => set({ statement: e.target.value })}
            className={textareaCls}
            data-testid="exhibition-field-statement"
            placeholder="What does this exhibition argue?"
          />
        </Field>
        <Field
          label="Description"
          helper="Practical/public description of what the exhibition is."
        >
          <textarea
            rows={3}
            value={form.description || ''}
            onChange={(e) => set({ description: e.target.value })}
            className={textareaCls}
            data-testid="exhibition-field-description"
          />
        </Field>
        <Field
          label="AI Context Notes"
          helper="Internal notes for the AI idea agent — constraints, priorities, production realities, audience, curatorial preferences, and what ideas should be judged against."
        >
          <textarea
            rows={4}
            value={form.ai_context_notes || ''}
            onChange={(e) => set({ ai_context_notes: e.target.value })}
            className={textareaCls}
            data-testid="exhibition-field-ai_context_notes"
            placeholder="Hard constraints, what to prioritize, what to avoid…"
          />
        </Field>
      </section>

      {/* Location */}
      <section className="space-y-3">
        <SectionHeader title="Location" helper="Physical venue and city." />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Location Name">
            <input
              value={form.location_name || ''}
              onChange={(e) => set({ location_name: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Address">
            <input
              value={form.location_address || ''}
              onChange={(e) => set({ location_address: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="City">
            <input
              value={form.city || ''}
              onChange={(e) => set({ city: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Country">
            <input
              value={form.country || ''}
              onChange={(e) => set({ country: e.target.value })}
              className={inputCls}
            />
          </Field>
        </div>
      </section>

      {/* Dates */}
      <section className="space-y-3">
        <SectionHeader title="Dates" helper="Opening, closing, and open-call deadline." />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Start Date *">
            <input
              type="date"
              required
              value={toDateInput(form.start_date)}
              onChange={(e) => set({ start_date: e.target.value })}
              className={inputCls}
              data-testid="exhibition-field-start_date"
            />
          </Field>
          <Field label="End Date *">
            <input
              type="date"
              required
              value={toDateInput(form.end_date)}
              onChange={(e) => set({ end_date: e.target.value })}
              className={inputCls}
              data-testid="exhibition-field-end_date"
            />
          </Field>
          <Field label="Open Call Deadline">
            <input
              type="date"
              value={toDateInput(form.open_call_deadline)}
              onChange={(e) => set({ open_call_deadline: e.target.value })}
              className={inputCls}
            />
          </Field>
        </div>
      </section>

      {/* Toggles */}
      <section className="space-y-3">
        <SectionHeader title="Public Flows" helper="Enable or disable visitor flows on the public site." />
        <div className="flex flex-wrap gap-2">
          <Toggle
            label="Tickets enabled"
            value={!!form.ticket_requests_enabled}
            onChange={(v) => set({ ticket_requests_enabled: v })}
            testId="toggle-tickets"
          />
          <Toggle
            label="Open Call enabled"
            value={!!form.open_call_enabled}
            onChange={(v) => set({ open_call_enabled: v })}
            testId="toggle-opencall"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
          <Field label="Ticket mode" helper="Standard = email ticket · BAF = collect data + reveal claim button">
            <select
              value={form.ticket_mode || 'standard'}
              onChange={(e) => set({ ticket_mode: e.target.value })}
              className={inputCls}
              data-testid="exhibition-field-ticket_mode"
            >
              <option value="standard">Standard — email ticket</option>
              <option value="baf_free">BAF free tickets — reveal claim button</option>
            </select>
          </Field>
          {form.ticket_mode === 'baf_free' && (
            <Field label="BAF claim link" helper="Opens under the reactive button after a visitor registers">
              <input
                type="url"
                value={form.baf_ticket_url || ''}
                onChange={(e) => set({ baf_ticket_url: e.target.value })}
                placeholder="https://britishartfair.seetickets.com/…"
                className={inputCls}
                data-testid="exhibition-field-baf_ticket_url"
              />
            </Field>
          )}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-[#1a1a1a]">
        <ActionButton
          variant="primary"
          icon={Save}
          type="submit"
          disabled={busy}
          data-testid={`exhibition-submit-${mode}`}
        >
          {mode === 'edit' ? 'Save Changes' : 'Create Exhibition'}
        </ActionButton>
        <ActionButton
          variant="secondary"
          icon={X}
          onClick={onCancel}
          disabled={busy}
          type="button"
        >
          Cancel
        </ActionButton>
      </div>
    </form>
  );
}

function ExhibitionEditDrawer({ exhibition, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const open = !!exhibition;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const submit = async (form) => {
    setBusy(true);
    try {
      const res = await axios.patch(`${API}/admin/exhibitions/${exhibition.id}`, cleanForApi(form));
      toast.success('Exhibition updated');
      onSaved && onSaved(res.data);
      onClose && onClose();
    } catch (err) {
      toast.error(serverError(err, 'Update failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/55 backdrop-blur-[2px] z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            data-testid="exhibition-edit-overlay"
          />
          <motion.aside
            className="fixed right-0 top-0 bottom-0 z-[101] w-full sm:w-[640px] bg-[#070707] border-l border-[#1a1a1a] overflow-y-auto flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
            data-testid="exhibition-edit-drawer"
          >
            <header className="sticky top-0 z-10 bg-[#070707]/95 backdrop-blur-sm border-b border-[#1a1a1a] px-6 py-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.25em] uppercase">
                  Edit Exhibition
                </p>
                <h2 className="font-outfit text-xl font-semibold text-white truncate mt-0.5">
                  {exhibition.title}
                </h2>
                <p className="text-xs font-mono text-[#888] mt-0.5">{exhibition.slug}</p>
              </div>
              <button
                onClick={onClose}
                data-clickable="true"
                data-testid="exhibition-edit-close"
                aria-label="Close"
                className="w-8 h-8 border border-[#222] text-[#888] hover:text-white hover:border-[#90E0EF]/40 flex items-center justify-center transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="p-6 flex-1">
              <ExhibitionForm
                initial={exhibition}
                onSubmit={submit}
                onCancel={onClose}
                mode="edit"
                busy={busy}
              />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function ExhibitionCard({ exhibition, isSelected, onEdit, onDelete }) {
  const hasStatement = !!exhibition.statement?.trim();
  const hasAi = !!exhibition.ai_context_notes?.trim();

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative border border-[#1a1a1a] bg-[#0d0d0d] p-5 hover:border-[#90E0EF]/25 transition-colors"
      data-testid={`exhibition-card-${exhibition.id}`}
      style={{
        borderLeft: isSelected ? '2px solid #90E0EF' : '2px solid transparent',
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-outfit text-lg font-semibold text-white">{exhibition.title}</h3>
            <StatusBadge status={exhibition.status || 'draft'} kind="open_call" />
            {isSelected && (
              <span className="text-[9px] font-mono text-[#90E0EF] tracking-[0.18em] uppercase">
                · Selected
              </span>
            )}
          </div>
          <p className="text-xs font-mono text-[#888] mt-1">{exhibition.slug}</p>
          {exhibition.theme && (
            <p className="text-[11px] font-mono text-[#aaa] mt-2 italic">
              — {exhibition.theme}
            </p>
          )}
          {exhibition.description && (
            <p className="text-xs font-mono text-[#bbb] mt-2 line-clamp-2 max-w-xl leading-relaxed">
              {exhibition.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ActionButton
            variant="primary"
            icon={Edit3}
            onClick={() => onEdit(exhibition)}
            dense
            data-testid={`exhibition-edit-${exhibition.id}`}
          >
            Edit
          </ActionButton>
          <ActionButton
            variant="ghost"
            icon={Trash2}
            onClick={() => onDelete(exhibition)}
            dense
            data-testid={`exhibition-delete-${exhibition.id}`}
            aria-label="Delete exhibition"
          >
            Delete
          </ActionButton>
        </div>
      </div>

      {/* Meta strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px mt-4">
        <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-3">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-3 h-3 text-[#90E0EF]/80" />
            <p className="text-[9px] font-mono text-[#777] tracking-[0.15em] uppercase">Dates</p>
          </div>
          <p className="text-xs font-mono text-white mt-1">
            {exhibition.start_date || '—'}{' '}
            <span className="text-[#444]">→</span>{' '}
            {exhibition.end_date || '—'}
          </p>
        </div>
        <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-3 h-3 text-[#90E0EF]/80" />
            <p className="text-[9px] font-mono text-[#777] tracking-[0.15em] uppercase">Location</p>
          </div>
          <p className="text-xs font-mono text-white mt-1 truncate">
            {[exhibition.city, exhibition.country].filter(Boolean).join(', ') || exhibition.location_name || '—'}
          </p>
        </div>
        <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-3">
          <div className="flex items-center gap-2">
            <TicketIcon className="w-3 h-3 text-[#90E0EF]/80" />
            <p className="text-[9px] font-mono text-[#777] tracking-[0.15em] uppercase">Tickets</p>
          </div>
          <p className="text-xs font-mono text-white mt-1">
            {exhibition.ticket_requests_enabled ? (
              <span className="text-[#7ed4a0]">Enabled</span>
            ) : (
              <span className="text-[#888]">Disabled</span>
            )}
          </p>
        </div>
        <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-3">
          <div className="flex items-center gap-2">
            <FileText className="w-3 h-3 text-[#90E0EF]/80" />
            <p className="text-[9px] font-mono text-[#777] tracking-[0.15em] uppercase">Open Call</p>
          </div>
          <p className="text-xs font-mono text-white mt-1">
            {exhibition.open_call_enabled ? (
              <span className="text-[#7ed4a0]">Enabled</span>
            ) : (
              <span className="text-[#888]">Disabled</span>
            )}
          </p>
        </div>
      </div>

      {/* Public URL — always shown; hash-anchored to the enabled CTAs */}
      {exhibition.slug && (
        <div className="mt-4">
          <ExhibitionPublicUrlStrip
            slug={exhibition.slug}
            label={
              exhibition.ticket_requests_enabled && exhibition.open_call_enabled
                ? 'Public site — tickets + open call'
                : exhibition.ticket_requests_enabled
                  ? 'Public site — tickets'
                  : exhibition.open_call_enabled
                    ? 'Public site — open call'
                    : 'Public site'
            }
            disabled={!exhibition.ticket_requests_enabled && !exhibition.open_call_enabled}
            disabledReason="Enable tickets or open call to expose intake forms on this URL."
          />
        </div>
      )}

      {/* Curatorial readiness chips */}
      <div className="flex flex-wrap gap-2 mt-4 text-[10px] font-mono uppercase tracking-[0.14em]">
        <span
          className="px-2 py-0.5 border"
          style={
            hasStatement
              ? { color: '#90E0EF', borderColor: 'rgba(144,224,239,0.4)' }
              : { color: '#555', borderColor: '#222' }
          }
        >
          {hasStatement ? 'Statement added' : 'No statement'}
        </span>
        <span
          className="px-2 py-0.5 border inline-flex items-center gap-1.5"
          style={
            hasAi
              ? { color: '#c89dff', borderColor: 'rgba(200,157,255,0.4)' }
              : { color: '#555', borderColor: '#222' }
          }
        >
          <Sparkles className="w-3 h-3" />
          {hasAi ? 'AI context ready' : 'AI context empty'}
        </span>
      </div>
    </motion.article>
  );
}

export default function ExhibitionsPage() {
  const {
    exhibitions: exList,
    setExhibitions,
    selectedExhibition,
    setSelectedExhibition,
  } = useOutletContext();

  const [showCreate, setShowCreate] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [busyCreate, setBusyCreate] = useState(false);

  const exhibitions = useMemo(() => exList || [], [exList]);

  const handleCreate = async (form) => {
    setBusyCreate(true);
    try {
      const res = await axios.post(`${API}/admin/exhibitions`, cleanForApi(form));
      setExhibitions((arr) => [res.data, ...(arr || [])]);
      setShowCreate(false);
      toast.success('Exhibition created');
    } catch (err) {
      toast.error(serverError(err, 'Creation failed'));
    } finally {
      setBusyCreate(false);
    }
  };

  const handleDelete = async (ex) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(`Delete "${ex.title}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/exhibitions/${ex.id}`);
      setExhibitions((arr) => (arr || []).filter((e) => e.id !== ex.id));
      if (selectedExhibition?.id === ex.id) {
        setSelectedExhibition(null);
      }
      toast.success('Exhibition deleted');
    } catch (err) {
      toast.error(serverError(err, 'Delete failed'));
    }
  };

  const handleSaved = (updated) => {
    setExhibitions((arr) => (arr || []).map((e) => (e.id === updated.id ? updated : e)));
    if (selectedExhibition && selectedExhibition.id === updated.id) {
      setSelectedExhibition(updated);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-semibold text-white tracking-tight">
            Exhibitions
          </h1>
          <p className="text-xs font-mono text-[#888] mt-1">
            {exhibitions.length} total · curatorial context drives the AI idea agent
          </p>
        </div>
        <ActionButton
          variant="primary"
          icon={Plus}
          onClick={() => setShowCreate(!showCreate)}
          data-testid="toggle-create-exhibition"
        >
          {showCreate ? 'Close form' : 'Create Exhibition'}
        </ActionButton>
      </div>

      <AnimatePresence>
        {showCreate && (
          <motion.section
            key="create"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="border border-[#1a1a1a] bg-[#0d0d0d] p-6"
            data-testid="exhibition-create-panel"
          >
            <ExhibitionForm
              initial={EMPTY_FORM}
              onSubmit={handleCreate}
              onCancel={() => setShowCreate(false)}
              mode="create"
              busy={busyCreate}
            />
          </motion.section>
        )}
      </AnimatePresence>

      <div className="space-y-3">
        {exhibitions.length === 0 ? (
          <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-12 text-center">
            <p className="text-[#666] font-mono text-sm">No exhibitions yet</p>
          </div>
        ) : (
          exhibitions.map((ex) => (
            <ExhibitionCard
              key={ex.id}
              exhibition={ex}
              isSelected={selectedExhibition?.id === ex.id}
              onEdit={setEditingItem}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>

      <ExhibitionEditDrawer
        exhibition={editingItem}
        onClose={() => setEditingItem(null)}
        onSaved={handleSaved}
      />
    </div>
  );
}
